import { fg } from '@atlassian/jira-feature-gating';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	createResource,
	type ResourceStoreContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import type { JecMigrationHubEnabledResponse } from './JecMigrationHubEnabled.types.tsx';

const canAccessJecMigrationHub = ({
	licensedProducts,
	isAdmin,
	isSiteAdmin,
}: ResourceStoreContext['tenantContext']): boolean => {
	const isProductAdmin = isAdmin || isSiteAdmin;
	return !!licensedProducts['jira-servicedesk'] && isProductAdmin;
};

/**
 * Resolves to `true` when the customer has at least one JEC channel that can be
 * migrated via the JEC Migration Hub, otherwise `false`.
 * The request is only made when the `jira-bifrost-jec-migration-hub` gate is on and the user
 * is a product admin (Jira admin or site admin) on a JSM licensed site (any edition).
 * Otherwise it resolves to `false` without a network request.
 * The result is cached for the whole session to avoid refetching on navigation.
 */
export const jecMigrationHubEnabledResource: RouteResource<boolean> = createResource<boolean>({
	type: 'JEC_MIGRATION_HUB_ENABLED',
	getKey: () => 'jec-migration-hub-enabled',
	getData: async (_routerDataContext, { tenantContext }) => {
		if (!fg('jira-bifrost-jec-migration-hub') || !canAccessJecMigrationHub(tenantContext)) {
			return false;
		}
		const response = await fetchJson<JecMigrationHubEnabledResponse>(
			`/gateway/api/jsm/ops/web/${tenantContext.cloudId}/v1/jec/migration-hub/enabled`,
		);
		return response?.data?.enabled === true;
	},
	maxAge: Infinity,
	isBrowserOnly: true,
});
